/**
 * Quick action cards grid for help center
 */

import { Link } from 'react-router-dom';
import {
  MessageSquare,
  PlayCircle,
  BookOpen,
  HelpCircle,
  Mail,
  Download,
  ExternalLink,
  LucideIcon,
} from 'lucide-react';
import { HelpQuickAction } from '../../types';

// Icon mapping for quick actions
const iconMap: Record<string, LucideIcon> = {
  'message-square': MessageSquare,
  'play-circle': PlayCircle,
  'book-open': BookOpen,
  'help-circle': HelpCircle,
  'mail': Mail,
  'download': Download,
  'external-link': ExternalLink,
};

interface HelpQuickActionsProps {
  actions: HelpQuickAction[];
}

export function HelpQuickActions({ actions }: HelpQuickActionsProps): JSX.Element {
  if (actions.length === 0) {
    return <></>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
      {actions.map(action => {
        const Icon = iconMap[action.icon] || HelpCircle;
        const isExternal = action.linkUrl.startsWith('http');

        const content = (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all text-center group">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary-50 dark:bg-primary-900/30 rounded-full group-hover:bg-primary-100 dark:group-hover:bg-primary-800/40 transition-colors">
                <Icon size={24} className="text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {action.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {action.description}
            </p>
            <span className="inline-flex items-center text-sm font-medium text-primary-600 dark:text-primary-400 group-hover:text-primary-700 dark:group-hover:text-primary-300">
              {action.buttonText}
              {isExternal && <ExternalLink size={14} className="ml-1" />}
            </span>
          </div>
        );

        if (isExternal) {
          return (
            <a
              key={action.id}
              href={action.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {content}
            </a>
          );
        }

        return (
          <Link key={action.id} to={action.linkUrl}>
            {content}
          </Link>
        );
      })}
    </div>
  );
}

export default HelpQuickActions;
